import {
  AttachedBlobAsset,
  QuoteItem,
  QuoteItemPrice,
  QuoteItemPriceBreakdown
} from '../../../api/dealer-api-interface-quote';
import { clone, compare } from '../../../../webmodule-common/other/clone';

import { DevelopmentError } from '../../../../webmodule-common/other/development-error';
import {
  FrameV2,
  QuoteFrameItemPrice,
  V6QuoteFrameEditorData,
  V6QuoteItemSupplierInfo
} from '../../../../webmodule-common/interop/webmodule-interop';
import { fromJsonStr, toJsonStr } from '../../../../webmodule-common/other/blob-converters';
import { globalState } from '../../../../webmodule-common/other/ui/global-state';
import { isEmptyOrSpace } from '../../../../webmodule-common/other/ui/string-helper-functions';
import { isFrame, isSupplierSpecialItem } from '../quote-helper-functions';
import { money } from '../../../../webmodule-common/other/currency-formatter';
import { publicFetch, secureFetch } from '../../../api/secure-fetch';
import { QuoteAssetType, QuoteItemAssetType } from '../../../api/quote-api';
import { QuoteItemContainer } from '../quote-item-container';
import { QuoteItemContainerV6Data } from './quote-item-container-expanded-v6-data';
import { quoteSupplierProvider } from '../quoteSupplierProvider';
import { StockLookupView } from '../../../api/dealer-api-interface-franchisee';
import { tenantId } from '../../../../webmodule-common/other/api/current-user';
import { v6Config, v6Util } from '../../../../webmodule-common/interop/interfaces/v6-config';

// The asset URL builders live here, so the tag that goes into those URLs is re-exported alongside
// them and callers need only one import. It is defined in its own module so it can be unit tested
// without dragging in this file's V6 dependency graph.
export { assetContentTag } from '../asset-content-tag';

export function getV6QuoteItemCache(quoteItemContainer: QuoteItemContainer): QuoteItemContainerV6Data | undefined {
  if (!isV6(quoteItemContainer)) return undefined;
  if (!quoteItemContainer.data?.providerData) return undefined;
  if (!quoteItemContainer.clientSideCache) {
    try {
      const v6Item =
        isFrame(quoteItemContainer.item) || isSupplierSpecialItem(quoteItemContainer.item)
          ? quoteItemContainer.data
            ? fromJsonStr<object>(quoteItemContainer.data.providerData)
            : undefined
          : undefined;

      const v6Frame = v6Item ? v6Util().getFrameData(v6Item) : undefined;
      const supplierInfo = v6Item ? v6Util().getQuoteItemSupplier(v6Item) : undefined;
      const cache: QuoteItemContainerV6Data = {
        frameData: v6Frame,
        supplier: supplierInfo,
        v6Item: v6Item ?? undefined
      };
      quoteItemContainer.clientSideCache = cache;
    } catch (e) {
      // Corrupt/invalid provider data (e.g. getFrameData throws "Invalid V6QuoteItemExternalStorage").
      // Treat as "no V6 data" - every caller already handles an undefined result by skipping the
      // item - rather than letting the throw break the whole view (e.g. supplier technical review).
      console.warn(`Unable to parse V6 data for quote item ${quoteItemContainer.item.id}`, e);
      return undefined;
    }
  }
  return quoteItemContainer.clientSideCache as QuoteItemContainerV6Data;
}

/**
 * Sets the providerData on a QuoteItemContainer and flushes the clientSideCache.
 * Always use this function instead of directly setting providerData to ensure cache consistency.
 */
export function setProviderData(container: QuoteItemContainer, data: string | null) {
  if (container.data) {
    container.data.providerData = data;
    container.clientSideCache = undefined;
  }
}

export function getV6QuoteItemForContainer(quoteItemContainer: QuoteItemContainer): object | undefined {
  return getV6QuoteItemCache(quoteItemContainer)?.v6Item;
}

export function getV6FrameForContainer(quoteItemContainer: QuoteItemContainer): FrameV2 | undefined {
  return getV6QuoteItemCache(quoteItemContainer)?.frameData;
}

export function getV6SupplierForContainer(quoteItemContainer: QuoteItemContainer): V6QuoteItemSupplierInfo | undefined {
  return getV6QuoteItemCache(quoteItemContainer)?.supplier;
}

export function getQuantityCostWithBuyIn(netSellPrice: number, buyInCosts?: StockLookupView[]): number {
  //Buy in cost are for the whole BOM. We need to divide the sum by qty and add it to the single unit
  if (buyInCosts) {
    const buyInSum = buyInCosts.reduce((sum, b) => sum + (b.cost ?? 0), 0);
    return money(netSellPrice + buyInSum, 2);
  }

  //due to rounding errors on reports we have decided to round all single unit prices to 2dp, and then reset quantity price
  //to be a multiplier of this value.
  return money(netSellPrice, 2);
}

export function isEmptyObject(obj) {
  return Object.keys(obj).length === 0 && obj.constructor === Object;
}

export interface SanitizedV6QuoteItemWithAssets {
  sanitizedItem: unknown;
  buyIn?: unknown;
  vrml?: unknown;
  bomView?: unknown;
}

/**
 * Asset keys that hold the item's actual content rather than an output derived from it.
 * These are always on: once set they are replaced, never removed. If V6 hands us nothing
 * for one of them the correct reading is "unchanged", NOT "cleared" - a failed image
 * render must not cost the frame its stored thumbnail. The server enforces the same rule
 * (QuoteAssetDeletionPolicy) and rejects a delete of either key.
 */
const alwaysPresentAssetKeys: ReadonlySet<string> = new Set(['data', 'thumbnail']);

export function quoteItemAssets(
  assets: Partial<Record<QuoteItemAssetType, string | unknown | null>>
): Partial<Record<QuoteItemAssetType, AttachedBlobAsset>> {
  const result: Partial<Record<QuoteItemAssetType, AttachedBlobAsset>> = {};

  for (const [assetType, data] of Object.entries(assets)) {
    const key = assetType as QuoteItemAssetType;
    const extension = getExtensionForAssetType(key);
    const assetData = typeof data !== 'string' ? toJsonStr(data) : data;
    const hasContent = !(assetData === null || assetData === undefined);

    // Derived assets (bom, bomView, model3d) go stale when data changes, so absence means
    // "clear it in case it used to exist". Content assets are omitted instead: sending no
    // key at all is the server's "no change" signal.
    if (!hasContent && alwaysPresentAssetKeys.has(key)) {
      continue;
    }

    result[key] = {
      data: assetData ?? null,
      assetPath: createQuoteItemAssetPath(key, extension),
      delete: !hasContent,
      hasContent: hasContent,
      dataBase64: null
    };
  }

  return result;

  function getExtensionForAssetType(assetType: QuoteItemAssetType): string {
    switch (assetType) {
      case 'model3d':
        return '.vrml';
      case 'bomView':
        return '.json';
      case 'thumbnail':
        return '.svg';
      case 'data':
        return '.json';
      case 'bom':
        return '.json';
      default:
        return '.bin';
    }
  }
}

export function splitV6QuoteItemAfterCommit(quoteItem: unknown): SanitizedV6QuoteItemWithAssets {
  if (!quoteItem || typeof quoteItem !== 'object') {
    return { sanitizedItem: quoteItem };
  }

  const { buyIn, vrml, bomView, ...sanitizedItem } = quoteItem as Record<string, string | null>;

  return {
    sanitizedItem,
    buyIn,
    vrml,
    bomView
  };
}

globalState().debugPricing = false;

/**
 * Formats the note kept against a supplier price adjustment when the adjustment is discarded
 * by a recalculation (the flag is disabled but the value is retained for audit). This mirrors
 * the convention used by the single-item supplier price-adjust editor so every recalculation
 * path records the discarded adjustment identically.
 *
 * Idempotent: re-applying it does not stack the value prefix (e.g. "900, 900, ...").
 * Returns '' when there is no value to retain.
 */
export function formatRetainedSupplierAdjustmentReason(
  adjustmentValue: number,
  reason: string | null | undefined
): string {
  if (adjustmentValue === 0) return '';
  const prefix = `${adjustmentValue}, `;
  let base = reason ?? '';
  while (base.startsWith(prefix)) {
    base = base.slice(prefix.length);
  }
  return `${adjustmentValue}, ${base}`;
}

export function applyV6PricesToQuoteItemPriceWithSupplierAdjustmentCorrections(
  quantity: number,
  price: QuoteFrameItemPrice,
  itemPrice: QuoteItemPrice,
  buyInCosts?: StockLookupView[]
) {
  const original = clone(itemPrice);
  //capture whether an active adjustment is being carried in, so we only rewrite the note
  //when a real adjustment is actually discarded (not on every ordinary recalculation).
  const hadActiveAdjustment = itemPrice.supplierPriceAdjustmentEnabled && itemPrice.supplierPriceAdjustment !== 0;
  applyV6PricesToQuoteItemPrice(quantity, price, itemPrice, buyInCosts);
  //supplier price adjustments offer a compounded price.
  //to workout if we need to reset the supplier adjustment, we must run it twice
  if (!compare(original, itemPrice)) {
    //    if (globalState().debugPricing) console.log(compareAnalyze(original, itemPrice));
    itemPrice.supplierPriceAdjustmentEnabled = false;
    if (hadActiveAdjustment) {
      //the adjustment is being discarded by the recalculation. Retain its value in the note
      //exactly as editing the item directly from the quote items page does.
      itemPrice.supplierPriceAdjustmentReason = formatRetainedSupplierAdjustmentReason(
        itemPrice.supplierPriceAdjustment,
        itemPrice.supplierPriceAdjustmentReason
      );
    }
    applyV6PricesToQuoteItemPrice(quantity, price, itemPrice, buyInCosts);
  }
}

export function applyV6PricesToQuoteItemPrice(
  quantity: number,
  price: QuoteFrameItemPrice,
  itemPrice: QuoteItemPrice | QuoteItemPriceBreakdown,
  buyInCosts?: StockLookupView[]
) {
  /*

   grossSellPrice = grossSellPrice + grossPriceLab
   netSellPrice = netSellPrice + netSellPriceLab
   const examplePriceData = {
   costPrice: 228.58,
   costTax: 22.86, //tax to add
   overHead: 114.29,
   markup: 85.73,
   grossSellPrice: 428.6, // RRP
   itemDisc: 60,
   salesCommission: 0,
   priceAdj: 0,
   netSellPrice: 368.6, // after discounts and commisions and adjustments. Dealer Price
   tax: 184.3, //tax to add
   costPriceLab: 0,
   costTaxLab: 0,
   overHeadLab: 0,
   markupLab: 0,
   grossSellPriceLab: 0,
   itemDiscLab: 0,
   salesCommissLab: 0,
   priceAdjLab: 0,
   netSellPriceLab: 0,
   taxLab: 0,
   grossSellPriceLockAdj: 0,
   grossSellPriceLockAdjLab: 0,
   unRecoverableWaste: 0,
   totalSellingPriceAdjusted: 0,
   //Dealer Price + all taxes etc
   totalSellPrice: 575.76,
   };
   */

  //This is the V6 Frame Price for all qty..
  const netSellPriceQuantity = price.netSellPrice + price.netSellPriceLab;
  //Kyley- Removed the code that was altering values based on the
  //supplier adjustment being wrong.
  //if we want to do that, we should enforce it at a higher layer so
  //that we are not manipulating it different from what the supplier expects
  const grossSellPriceQuantity = price.grossSellPrice + price.grossSellPriceLab;
  const itemPriceSupplierAdjustment = (itemPrice as QuoteItemPrice)?.supplierPriceAdjustmentEnabled
    ? ((itemPrice as QuoteItemPrice)?.supplierPriceAdjustment ?? 0)
    : 0;
  // This is v6 price included with buyin data and rounded to 2dp
  const quantityUnitCostWithBuyIn = getQuantityCostWithBuyIn(
    netSellPriceQuantity + itemPriceSupplierAdjustment,
    buyInCosts
  );

  const singleUnitCostWithBuyIn = money(quantityUnitCostWithBuyIn / quantity, 2);

  //remove the commonid and leave a dictionary of numbers for the raw data
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { quoteItemCommonId, currency, ...priceDict } = price;
  itemPrice.sourceData = priceDict;

  //set the single unit cost the to base the margin on
  itemPrice.singleUnitCost = singleUnitCostWithBuyIn; //2dp

  //reverse engineer the quantity cost. This wont match the v6 price anymore
  itemPrice.quantityCost = quantityUnitCostWithBuyIn;

  //this is the price that goes back to v6 generating this quote item again for supplier
  //this price now may have a reconciliation variance with the original v6 price due to
  //division but wont ever be more than 1cent
  itemPrice.supplierNettQuantityCost = money(netSellPriceQuantity, 2);

  //this is allocating the price the supplier is selling to the dealer
  itemPrice.supplierNettSingleUnitCost = money(itemPrice.supplierNettQuantityCost / quantity, 2);

  //This is changing in purpose, as the original intent of having taxes is redundant. we can look at raw source data
  //if needed
  //this value is storing the LISTRRP price
  //this is the price before Discount and SalesCommissions are applied
  itemPrice.supplierGrossQuantityCost = money(grossSellPriceQuantity, 2);
  //This is changing in purpose, as the original intent of having taxes is redundant. we can look at raw source data
  //if needed
  //this value is storing the LISTRRP price
  //this is the price before Discount and SalesCommissions are applied
  itemPrice.supplierGrossSingleUnitCost = money(itemPrice.supplierGrossQuantityCost / quantity, 2);
}

export function updateDealerFreehandPrice(quantity: number, price: QuoteItemPrice) {
  price.singleUnitCost = money(price.singleUnitCost, 2);
  //for now, quantityCost, supplierNettQuantityCost and supplierGrossQuantityCost should all 3 be the same value
  const qtyCost = money(price.singleUnitCost * quantity, 2);
  price.supplierNettSingleUnitCost = price.singleUnitCost;
  price.quantityCost = qtyCost;
  price.supplierNettQuantityCost = qtyCost;
  price.supplierGrossQuantityCost = qtyCost;
  price.supplierGrossSingleUnitCost = price.singleUnitCost;
  price.sourceData = {};
}

export function isV6(quoteItemContainer: QuoteItemContainer | QuoteItem): boolean {
  const item = (quoteItemContainer as QuoteItemContainer).item ?? (quoteItemContainer as QuoteItem) ?? null;
  return item ? item.serviceProvider === quoteSupplierProvider.v6 : false;
}

export function isQuoteFrameVersionCurrent(quoteItemContainer: QuoteItemContainer): boolean {
  const supplierInfo = getV6SupplierForContainer(quoteItemContainer);
  if (isEmptyOrSpace(supplierInfo?.supplierId)) return false;
  return supplierInfo?.isVersionCurrent ?? false;
}

export function getQuoteFrameVersion(quoteItemContainer: QuoteItemContainer): string {
  const supplierInfo = getV6SupplierForContainer(quoteItemContainer);
  if (!supplierInfo) return '';
  if (isEmptyOrSpace(supplierInfo.supplierId)) return '';
  return supplierInfo.quoteItemVersion;
}

export function getSupplierVersion(supplierId: string): string {
  const supplier = v6Config().getSupplier(supplierId);
  return supplier.version;
}

export function isSupplierUnitsMetric(supplierId: string): boolean {
  const supplier = v6Config().getSupplier(supplierId);
  return supplier.isMetric;
}

export function isSupplierUsingSSI(supplierId: string): boolean {
  const supplierInfo = v6Config().getSupplier(supplierId);

  return supplierInfo.options?.['useServerSideItems'] === 'true';
}

export function updateQuoteItemContainer(
  itemContainer: QuoteItemContainer,
  v6QuoteItemJsonStr: string | null | undefined,
  price: QuoteFrameItemPrice | null | undefined,
  buyInCosts?: StockLookupView[]
) {
  // Defensive: if the caller indicates there is no V6 quote item payload, do not
  // proceed with a partial update that would wipe providerData while still
  // updating prices or thumbnails. Treat this as a hard client-side error.
  if (isEmptyOrSpace(v6QuoteItemJsonStr)) {
    throw new DevelopmentError(
      'updateQuoteItemContainer: v6 quote item JSON is null, empty, whitespace, or empty GUID'
    );
  }

  setProviderData(itemContainer, v6QuoteItemJsonStr!);

  //only update the data if we passed in a value, otherwise leave it in its current value
  if (buyInCosts) itemContainer.buyInData = toJsonStr(buyInCosts);

  if (itemContainer.price && price) {
    const quantity = itemContainer.item.quantity;

    applyV6PricesToQuoteItemPriceWithSupplierAdjustmentCorrections(quantity, price, itemContainer.price, buyInCosts);
  }
}

export function convertV6CommitDetails(details: V6QuoteFrameEditorData) {
  const bomView = details.bomView;
  const vrml = details.vrml;
  const thumbnail = details.thumbnail;
  const v6QuoteItemStorage = details.quoteItem;
  // Piece-level bom is its own asset (bom.json) rather than embedded in
  // the data blob - the optimizer is the only consumer and the BOM JSON
  // can be large, so we keep it out of the hot data load path.
  const bom = details.bom;
  const assets = quoteItemAssets({
    data: v6QuoteItemStorage,
    thumbnail: thumbnail,
    bomView: bomView,
    model3d: vrml,
    bom: bom
  });
  return assets;
}

export function virtualPathToUrl(virtualPath: string): string {
  const host = globalThis.dealerConfiguration.apiHost ?? '';
  return `${host}/api/File/${virtualPath}`;
}

export function convertQuoteItemAssetPathToVirtualPath(
  quoteId: string | undefined,
  quoteItemId: string | undefined,
  assetPath: string | undefined
): string {
  if (!quoteId || !quoteItemId || !assetPath) return '';
  if (assetPath.startsWith('T')) {
    return assetPath;
  }
  if (assetPath.startsWith('/')) {
    assetPath = assetPath.substring(1);
  }
  if (assetPath.startsWith('./')) {
    assetPath = assetPath.substring(2);
  }
  return `T${tenantId()}/services/quote/${quoteId}/quoteItem/${quoteItemId}/${assetPath}`.replaceAll('//', '/');
}
export function convertQuoteItemAssetPathToUrl(
  quoteId: string | undefined,
  quoteItemId: string | undefined,
  assetPath: string | undefined,
  tag?: string
): string {
  if (!quoteId || !quoteItemId || !assetPath) return '';
  const virtualPath = convertQuoteItemAssetPathToVirtualPath(quoteId, quoteItemId, assetPath);
  const path = virtualPathToUrl(virtualPath);
  return tag ? `${path}?tag=${encodeURIComponent(tag)}` : path;
}

export function convertQuoteAssetPathToVirtualPath(quoteId: string | undefined, assetPath: string | undefined): string {
  if (!quoteId || !assetPath) return '';
  if (assetPath.startsWith('T')) {
    return assetPath;
  }
  if (assetPath.startsWith('/')) {
    assetPath = assetPath.substring(1);
  }
  if (assetPath.startsWith('./')) {
    assetPath = assetPath.substring(2);
  }
  return `T${tenantId()}/services/quote/${quoteId}/${assetPath}`.replaceAll('//', '/');
}
export function convertQuoteAssetPathToUrl(
  quoteId: string | undefined,
  assetPath: string | undefined,
  tag?: string
): string {
  if (!quoteId || !assetPath) return '';
  const virtualPath = convertQuoteAssetPathToVirtualPath(quoteId, assetPath);
  const path = virtualPathToUrl(virtualPath);
  // Quote-level assets are overwritten in place just like item-level ones, so they need the same
  // content tag - see assetContentTag. This parameter was previously absent, leaving them with a URL
  // that could never change.
  return tag ? `${path}?tag=${encodeURIComponent(tag)}` : path;
}

export function createFullQuoteAssetPath(quoteId: string, assetType: QuoteItemAssetType, ext: string) {
  const extension = ext.startsWith('.') ? ext : `.${ext}`;
  return `T${tenantId()}/services/quote/${quoteId}/assets/${assetType}${extension}`;
}
export function createQuoteAssetPath(assetType: QuoteAssetType, ext: string) {
  const extension = ext.startsWith('.') ? ext : `.${ext}`;
  return `./assets/${assetType}${extension}`;
}

export function createFullQuoteItemAssetPath(
  quoteId: string,
  quoteItemId: string,
  assetType: QuoteItemAssetType,
  ext: string
): string {
  const extension = ext.startsWith('.') ? ext : `.${ext}`;
  return `T${tenantId()}/services/quote/${quoteId}/quoteItem/${quoteItemId}/assets/${assetType}${extension}`;
}
export function createQuoteItemAssetPath(assetType: QuoteItemAssetType, ext: string): string {
  const extension = ext.startsWith('.') ? ext : `.${ext}`;
  return `./assets/${assetType}${extension}`;
}

export function createAssetDocumentPath(origin: string, docType: string, name: string, ext: string): string {
  const extension = ext.startsWith('.') ? ext : `.${ext}`;
  const documentName = !name ? 'default' : name;
  return `./document/${origin}/${docType}/${documentName}${extension}`;
}

export async function fetchQuoteAsset(options: {
  quoteId: string;
  quoteItemId?: string;
  assetPath?: string;
  asJsonObject?: boolean;
  publicUrl?: string | null;
}): Promise<string | object | null | undefined> {
  const { quoteId, quoteItemId, assetPath, asJsonObject, publicUrl } = options;

  // Skip publicUrl if it's Azurite (localhost) - use secureFetch fallback instead
  const isAzurite = publicUrl && (publicUrl.includes('127.0.0.1:10000') || publicUrl.includes('localhost:10000'));

  if (!isEmptyOrSpace(publicUrl) && !isAzurite) {
    try {
      const publicData = await publicFetch(publicUrl!);
      if (publicData.ok) {
        const publicContent = await publicData.text();
        return asJsonObject ? (fromJsonStr<object>(publicContent) ?? null) : publicContent;
      } else {
        console.warn(`publicFetch returned ${publicData.status}, falling back to secureFetch`);
        // Fall through to secureFetch below
      }
    } catch (error) {
      console.warn('publicFetch failed, falling back to secureFetch:', error);
      // Fall through to secureFetch below
    }
  }

  if (isEmptyOrSpace(assetPath)) return undefined;
  const filePath = quoteItemId
    ? convertQuoteItemAssetPathToUrl(quoteId, quoteItemId, assetPath)
    : convertQuoteAssetPathToUrl(quoteId, assetPath);
  try {
    const data = await secureFetch(filePath);
    const content = await data.text();
    if (!data.ok) {
      throw new Error(`HTTP error! status: ${data.status}, ${content}`);
    }

    return asJsonObject ? (fromJsonStr<object>(content) ?? null) : content;
  } catch {
    return undefined;
  }
}
